import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Shield, Building2, FileText, Users,
  Clock, Search, Server, ShoppingCart, Receipt, X, Eye,
  ClipboardList, DollarSign, Package, Banknote, BarChart3,
  Calendar, RefreshCw, ChevronDown, ChevronUp, Hotel,
  Truck, CreditCard, Wrench, Zap, LayoutGrid, BookOpen,
  UserCheck, LogOut,
} from "lucide-react";
import { AuthContext } from "../App";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Utilidades de fecha ───────────────────────────────────────────────────────
const toLocalISODate = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const addDays = (isoDate, n) => {
  const d = new Date(`${isoDate}T12:00:00`);
  d.setDate(d.getDate() + n);
  return toLocalISODate(d);
};

const today      = toLocalISODate();
const yesterday  = addDays(today, -1);
const sevenDaysAgo = addDays(today, -6);

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-PY", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
};
const fmtTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" });
};
const fmtDateFull = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-PY", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
};

// Reemplaza UUIDs completos por versión corta legible (#56b8e567)
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const shortUUID = (str = "") => str.slice(0, 8);
const cleanDetalle = (text) => {
  if (!text) return text;
  return text.replace(UUID_RE, (m) => `#${shortUUID(m)}`);
};

const fmtRelative = (iso) => {
  if (!iso) return null;
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)     return "ahora mismo";
  if (diff < 3600)   return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400)  return `hace ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} días`;
  return fmtDate(iso);
};

// ── Módulos con ícono y label amigable ────────────────────────────────────────
const moduloConfig = {
  presupuestos:         { icon: <FileText className="w-3.5 h-3.5" />,    color: "text-blue-400",    bg: "bg-blue-500/10 border-blue-500/20",    label: "Presupuestos" },
  facturas:             { icon: <Receipt className="w-3.5 h-3.5" />,     color: "text-cyan-400",    bg: "bg-cyan-500/10 border-cyan-500/20",    label: "Facturas" },
  contratos:            { icon: <ClipboardList className="w-3.5 h-3.5" />, color: "text-indigo-400", bg: "bg-indigo-500/10 border-indigo-500/20", label: "Contratos" },
  empresas:             { icon: <Building2 className="w-3.5 h-3.5" />,   color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", label: "Clientes" },
  usuarios:             { icon: <Users className="w-3.5 h-3.5" />,       color: "text-purple-400",  bg: "bg-purple-500/10 border-purple-500/20", label: "Usuarios" },
  inventario:           { icon: <Server className="w-3.5 h-3.5" />,      color: "text-teal-400",    bg: "bg-teal-500/10 border-teal-500/20",    label: "Inventario" },
  compras:              { icon: <ShoppingCart className="w-3.5 h-3.5" />, color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/20", label: "Compras" },
  proveedores:          { icon: <Truck className="w-3.5 h-3.5" />,       color: "text-amber-400",   bg: "bg-amber-500/10 border-amber-500/20",  label: "Proveedores" },
  costos_fijos:         { icon: <DollarSign className="w-3.5 h-3.5" />,  color: "text-red-400",     bg: "bg-red-500/10 border-red-500/20",      label: "Costos fijos" },
  empleados:            { icon: <Users className="w-3.5 h-3.5" />,       color: "text-pink-400",    bg: "bg-pink-500/10 border-pink-500/20",    label: "Empleados" },
  ingresos_varios:      { icon: <Banknote className="w-3.5 h-3.5" />,    color: "text-lime-400",    bg: "bg-lime-500/10 border-lime-500/20",    label: "Ingresos varios" },
  pagos_proveedores:    { icon: <Banknote className="w-3.5 h-3.5" />,    color: "text-yellow-400",  bg: "bg-yellow-500/10 border-yellow-500/20", label: "Pagos prov." },
  notas_credito:        { icon: <Receipt className="w-3.5 h-3.5" />,     color: "text-rose-400",    bg: "bg-rose-500/10 border-rose-500/20",    label: "Notas crédito" },
  inventario_productos: { icon: <Package className="w-3.5 h-3.5" />,     color: "text-cyan-400",    bg: "bg-cyan-500/10 border-cyan-500/20",    label: "Productos" },
  transferencias:       { icon: <CreditCard className="w-3.5 h-3.5" />,  color: "text-sky-400",     bg: "bg-sky-500/10 border-sky-500/20",      label: "Transferencias" },
  hoteleria:            { icon: <Hotel className="w-3.5 h-3.5" />,       color: "text-fuchsia-400", bg: "bg-fuchsia-500/10 border-fuchsia-500/20", label: "Hotelería" },
  produccion:           { icon: <Wrench className="w-3.5 h-3.5" />,      color: "text-orange-300",  bg: "bg-orange-500/10 border-orange-500/20", label: "Producción" },
  soporte:              { icon: <BookOpen className="w-3.5 h-3.5" />,    color: "text-violet-400",  bg: "bg-violet-500/10 border-violet-500/20", label: "Soporte" },
  lectura_bombas:       { icon: <Zap className="w-3.5 h-3.5" />,         color: "text-yellow-300",  bg: "bg-yellow-500/10 border-yellow-500/20", label: "Bombas" },
  auth:                 { icon: <Shield className="w-3.5 h-3.5" />,      color: "text-blue-300",    bg: "bg-blue-500/10 border-blue-500/20",    label: "Autenticación" },
  cheques:              { icon: <LayoutGrid className="w-3.5 h-3.5" />,  color: "text-teal-300",    bg: "bg-teal-500/10 border-teal-500/20",    label: "Cheques" },
};

const accionConfig = {
  crear:             { color: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30", label: "Crear" },
  editar:            { color: "bg-amber-500/20 text-amber-400 border border-amber-500/30",       label: "Editar" },
  eliminar:          { color: "bg-red-500/20 text-red-400 border border-red-500/30",             label: "Eliminar" },
  anular:            { color: "bg-orange-500/20 text-orange-400 border border-orange-500/30",    label: "Anular" },
  ver:               { color: "bg-slate-500/20 text-slate-400 border border-slate-500/30",       label: "Ver" },
  login:             { color: "bg-blue-500/20 text-blue-400 border border-blue-500/30",          label: "Login" },
  logout:            { color: "bg-slate-500/20 text-slate-300 border border-slate-500/30",       label: "Logout" },
  exportar:          { color: "bg-violet-500/20 text-violet-400 border border-violet-500/30",    label: "Exportar" },
  pagar:             { color: "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30",          label: "Pagar" },
  cobrar:            { color: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30", label: "Cobrar" },
  movimiento_manual: { color: "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30",          label: "Movimiento" },
  transferencia:     { color: "bg-sky-500/20 text-sky-400 border border-sky-500/30",             label: "Transferencia" },
  pago:              { color: "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30",          label: "Pago" },
  checkin:           { color: "bg-teal-500/20 text-teal-400 border border-teal-500/30",          label: "Check-in" },
  checkout:          { color: "bg-fuchsia-500/20 text-fuchsia-400 border border-fuchsia-500/30", label: "Check-out" },
};

const ROLE_LABEL = {
  super_admin: { label: "Super Admin", color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  admin:       { label: "Admin",       color: "text-purple-400 bg-purple-500/10 border-purple-500/20" },
  gerente:     { label: "Gerente",     color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  user:        { label: "Usuario",     color: "text-slate-400 bg-slate-500/10 border-slate-500/20" },
};

// ── Jerarquía de roles para forzar logout ────────────────────────────────────
const ROLE_RANK = { super_admin: 4, admin: 3, gerente: 2, user: 1 };

function canForceLogout(caller, target) {
  if (!caller || !target) return false;
  if (caller.id === target.id) return false;
  const callerRank = ROLE_RANK[caller.role] || 0;
  const targetRank = ROLE_RANK[target.role] || 1;
  if (callerRank > targetRank) return true;
  // usuario restringido con permiso granular solo puede sobre otros usuarios restringidos
  if (
    caller.role === "user" &&
    (caller.permisos || []).includes("auditoria.forzar_logout") &&
    target.role === "user"
  ) return true;
  return false;
}

const avatarColors = [
  "bg-blue-500/25 text-blue-300", "bg-purple-500/25 text-purple-300",
  "bg-emerald-500/25 text-emerald-300", "bg-amber-500/25 text-amber-300",
  "bg-rose-500/25 text-rose-300", "bg-cyan-500/25 text-cyan-300",
  "bg-indigo-500/25 text-indigo-300", "bg-pink-500/25 text-pink-300",
];
const avatarColor = (name = "") => avatarColors[name.charCodeAt(0) % avatarColors.length] || avatarColors[0];
const initials = (name = "") => name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";

const getModuleStyle = (m) =>
  moduloConfig[m] || { icon: <Shield className="w-3.5 h-3.5" />, color: "text-slate-400", bg: "bg-slate-500/10 border-slate-500/20", label: m };

const getAccionStyle = (accion = "") => {
  const k = Object.keys(accionConfig).find(k => accion.toLowerCase().includes(k));
  return accionConfig[k] || { color: "bg-slate-700/50 text-slate-400 border border-white/10", label: accion };
};

// ── Modal detalle ─────────────────────────────────────────────────────────────
function LogModal({ log, onClose }) {
  const [showTecnico, setShowTecnico] = useState(false);
  if (!log) return null;

  const modStyle  = getModuleStyle(log.modulo);
  const accStyle  = getAccionStyle(log.accion);
  const roleStyle = ROLE_LABEL[log.usuario_role] || ROLE_LABEL.user;

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-lg shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${modStyle.bg} border`}>
              <span className={modStyle.color}>{modStyle.icon}</span>
            </div>
            <div>
              <p className="text-white font-semibold text-sm">{modStyle.label}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${accStyle.color}`}>
                {log.accion}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Usuario */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-white/3 border border-white/5">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${avatarColor(log.usuario_nombre)}`}>
              {initials(log.usuario_nombre)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium">{log.usuario_nombre || "—"}</p>
              <p className="text-slate-500 text-xs truncate">{log.usuario_email || "—"}</p>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${roleStyle.color}`}>
              {roleStyle.label}
            </span>
          </div>

          {/* Fecha y empresa */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-white/3 border border-white/5">
              <p className="text-slate-500 text-xs mb-1">Fecha y hora</p>
              <p className="text-white text-sm font-medium">{fmtDate(log.fecha)}</p>
              <p className="text-slate-400 text-xs">{fmtTime(log.fecha)}</p>
            </div>
            <div className="p-3 rounded-lg bg-white/3 border border-white/5">
              <p className="text-slate-500 text-xs mb-1">Empresa</p>
              <p className="text-white text-sm font-medium">{log.logo_tipo || <span className="text-slate-600">—</span>}</p>
            </div>
          </div>

          {/* Detalle */}
          {log.detalle && (
            <div className="p-3 rounded-lg bg-white/3 border border-white/5">
              <p className="text-slate-500 text-xs mb-1">Detalle</p>
              <p className="text-slate-200 text-sm leading-relaxed">{cleanDetalle(log.detalle)}</p>
            </div>
          )}

          {/* Fecha exacta */}
          <div className="text-xs text-slate-700 text-right pt-1">
            {fmtDateFull(log.fecha)}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ── Badge actividad usuario ───────────────────────────────────────────────────
function UserStatusBadge({ u }) {
  if (u.activo_ahora) {
    const mins = u.inactive_minutes ?? 0;
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        {mins === 0 ? "Activo ahora" : `Activo (${mins}m)`}
      </span>
    );
  }
  const last = u.last_activity_at ? fmtRelative(u.last_activity_at) : null;
  if (!last) {
    return <span className="text-xs text-slate-600">Sin actividad</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-500" title={fmtDateFull(u.last_activity_at)}>
      <Clock className="w-3 h-3" />{last}
    </span>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
const AuditoriaPage = () => {
  const { token, user } = useContext(AuthContext);

  const [logs, setLogs]             = useState([]);
  const [forcingLogout, setForcingLogout] = useState(null); // user_id en proceso
  const [loading, setLoading]       = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);

  // Filtros
  const [filterMode, setFilterMode] = useState("hoy");
  const [fechaDesde, setFechaDesde] = useState(today);
  const [fechaHasta, setFechaHasta] = useState(today);
  const [searchQ, setSearchQ]       = useState("");
  const [filtroAccion, setFiltroAccion] = useState("");

  // Usuarios
  const [usuarios, setUsuarios]           = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [lastRefreshUsuarios, setLastRefreshUsuarios] = useState(null);
  const usuariosIntervalRef = useRef(null);
  const usuariosRef         = useRef(null);

  // Hora del servidor — para chequear a simple vista que el reloj del
  // servidor está sincronizado. Se pide el offset (servidor − navegador) una
  // vez y se refresca cada 60s; el reloj en pantalla "tickea" cada segundo
  // aplicando ese offset sobre Date.now(), así se ve corriendo en vivo sin
  // tener que pedirle la hora al backend a cada segundo.
  const [srvOffsetMs, setSrvOffsetMs] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    let alive = true;
    const fetchServerTime = () => {
      fetch(`${API}/admin/auditoria/hora-servidor`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!alive || !data?.utc) return;
          setSrvOffsetMs(new Date(data.utc).getTime() - Date.now());
        })
        .catch(() => {});
    };
    fetchServerTime();
    const iv = setInterval(fetchServerTime, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [token]);

  useEffect(() => {
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  const horaServidorTexto = srvOffsetMs != null
    ? new Date(nowTick + srvOffsetMs).toLocaleString("es-PY", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      })
    : null;

  const getDateRange = useCallback((mode) => {
    if (mode === "hoy")    return { desde: today,       hasta: today };
    if (mode === "ayer")   return { desde: yesterday,   hasta: yesterday };
    if (mode === "semana") return { desde: sevenDaysAgo, hasta: today };
    return { desde: fechaDesde, hasta: fechaHasta };
  }, [fechaDesde, fechaHasta]);

  const fetchLogs = useCallback(async (mode, q = searchQ, accion = filtroAccion) => {
    const m = mode ?? filterMode;
    setLoading(true);
    try {
      const { desde, hasta } = getDateRange(m);
      const params = new URLSearchParams({ fecha_desde: desde, fecha_hasta: hasta, limit: "500" });
      if (q?.trim())    params.set("q", q.trim());
      if (accion)       params.set("accion", accion);
      const res = await fetch(`${API}/admin/auditoria?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setLogs(await res.json());
    } catch (err) {
      console.error("Error fetching audit logs:", err);
    } finally {
      setLoading(false);
    }
  }, [token, filterMode, getDateRange, searchQ, filtroAccion]);

  const fetchUsuarios = useCallback(async () => {
    setLoadingUsuarios(true);
    try {
      const res = await fetch(`${API}/admin/usuarios-activos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setUsuarios(await res.json());
        setLastRefreshUsuarios(new Date());
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoadingUsuarios(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLogs("hoy", "", "");
    fetchUsuarios();
    usuariosIntervalRef.current = setInterval(fetchUsuarios, 30000);
    return () => clearInterval(usuariosIntervalRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterMode = (mode) => {
    setFilterMode(mode);
    if (mode !== "personalizado") fetchLogs(mode);
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") fetchLogs(filterMode, searchQ, filtroAccion);
  };

  const handleAccionFilter = (accion) => {
    const next = accion === filtroAccion ? "" : accion;
    setFiltroAccion(next);
    fetchLogs(filterMode, searchQ, next);
  };

  const periodLabel = () => {
    if (filterMode === "hoy")    return "Hoy";
    if (filterMode === "ayer")   return "Ayer";
    if (filterMode === "semana") return "Últimos 7 días";
    return fechaDesde === fechaHasta ? fechaDesde : `${fechaDesde} → ${fechaHasta}`;
  };

  const handleForceLogout = useCallback(async (targetUser) => {
    if (!window.confirm(`¿Cerrar la sesión de ${targetUser.name}?`)) return;
    setForcingLogout(targetUser.id);
    try {
      const res = await fetch(`${API}/admin/auditoria/forzar-logout/${targetUser.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setUsuarios(prev => prev.map(u =>
          u.id === targetUser.id ? { ...u, activo_ahora: false } : u
        ));
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || "No se pudo cerrar la sesión");
      }
    } catch {
      alert("Error de conexión");
    } finally {
      setForcingLogout(null);
    }
  }, [token]);

  const activeNow  = usuarios.filter(u => u.activo_ahora).length;
  const totalUsers = usuarios.length;

  const QUICK_FILTERS = [
    { accion: "crear",   label: "Crear",   color: "emerald" },
    { accion: "editar",  label: "Editar",  color: "amber" },
    { accion: "eliminar",label: "Eliminar",color: "red" },
    { accion: "login",   label: "Login",   color: "blue" },
    { accion: "logout",  label: "Logout",  color: "slate" },
  ];

  return (
    <div className="p-4 md:p-8">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <Link to="/sistema" className="text-slate-400 hover:text-arandu-blue flex items-center gap-2 mb-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Volver al Dashboard
          </Link>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Shield className="w-8 h-8 text-amber-400" /> Auditoría
          </h1>
          <p className="text-slate-400 text-sm mt-1">Registro de todas las acciones administrativas</p>
          {horaServidorTexto && (
            <div className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg text-slate-400 text-xs">
              <Clock className="w-3.5 h-3.5 text-amber-400/80" />
              Hora del servidor: <span className="text-slate-200 font-medium">{horaServidorTexto}</span>
              <span className="text-slate-500">(Paraguay)</span>
            </div>
          )}
        </div>
        <button
          onClick={() => usuariosRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/25 rounded-xl text-sm font-medium transition-colors self-start md:self-auto"
        >
          <UserCheck className="w-4 h-4" />
          Usuarios ({activeNow} activos / {totalUsers})
        </button>
      </div>

      {/* Filtros periodo */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-arandu-dark border border-white/10 rounded-xl p-1">
          {[
            { mode: "hoy",           label: "Hoy" },
            { mode: "ayer",          label: "Ayer" },
            { mode: "semana",        label: "7 días" },
            { mode: "personalizado", label: "Personalizado" },
          ].map(({ mode, label }) => (
            <button key={mode} onClick={() => handleFilterMode(mode)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                filterMode === mode
                  ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  : "text-slate-400 hover:text-white"
              }`}>{label}</button>
          ))}
        </div>

        <div className={`flex items-center gap-2 transition-opacity ${filterMode === "personalizado" ? "opacity-100" : "opacity-30 pointer-events-none"}`}>
          <Calendar className="w-4 h-4 text-slate-500" />
          <DateInput value={fechaDesde} max={fechaHasta}
            onChange={e => setFechaDesde(e.target.value)}
            className="bg-arandu-dark border border-white/10 text-white text-sm rounded-lg px-2.5 py-1.5 outline-none focus:border-amber-500/50" />
          <span className="text-slate-500 text-xs">hasta</span>
          <DateInput value={fechaHasta} min={fechaDesde} max={today}
            onChange={e => setFechaHasta(e.target.value)}
            className="bg-arandu-dark border border-white/10 text-white text-sm rounded-lg px-2.5 py-1.5 outline-none focus:border-amber-500/50" />
          <button onClick={() => fetchLogs("personalizado")}
            className="px-3 py-1.5 text-sm bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/30 transition-colors">
            Buscar
          </button>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {!loading && (
            <span className="text-xs text-slate-500">
              {logs.length} registro{logs.length !== 1 ? "s" : ""} — <span className="text-slate-400">{periodLabel()}</span>
            </span>
          )}
          <button onClick={() => fetchLogs(filterMode)} disabled={loading}
            className="p-1.5 text-slate-500 hover:text-white transition-colors rounded" title="Refrescar registros">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Buscador global */}
      <div className="mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            onKeyDown={handleSearch}
            placeholder="Buscar por usuario, módulo, acción, detalle, empresa… (Enter para buscar)"
            className="w-full bg-arandu-dark border border-white/10 text-white text-sm rounded-xl pl-10 pr-4 py-2.5 outline-none focus:border-amber-500/40 placeholder-slate-600"
          />
          {searchQ && (
            <button onClick={() => { setSearchQ(""); fetchLogs(filterMode, "", filtroAccion); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filtros rápidos por acción */}
      <div className="mb-5 flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-600">Filtrar:</span>
        {QUICK_FILTERS.map(({ accion, label }) => {
          const style = getAccionStyle(accion);
          const active = filtroAccion === accion;
          return (
            <button key={accion} onClick={() => handleAccionFilter(accion)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                active ? style.color + " font-semibold" : "text-slate-500 border-white/10 hover:border-white/20 hover:text-slate-300"
              }`}>{label}</button>
          );
        })}
        {filtroAccion && (
          <button onClick={() => handleAccionFilter("")} className="text-xs text-slate-600 hover:text-slate-400 transition-colors">
            <X className="w-3 h-3 inline" /> Limpiar
          </button>
        )}
      </div>

      {/* Tabla de logs */}
      {loading ? (
        <div className="text-center py-12 text-arandu-blue animate-pulse">Cargando…</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 bg-arandu-dark-light border border-white/5 rounded-xl mb-8">
          <Shield className="w-14 h-14 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-400">No hay registros para este período</p>
        </div>
      ) : (
        <div className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden mb-8">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8 text-slate-500 text-xs uppercase tracking-wide">
                <th className="px-4 py-3 text-left w-32">Fecha / Hora</th>
                <th className="px-4 py-3 text-left">Usuario</th>
                <th className="px-4 py-3 text-left">Módulo</th>
                <th className="px-4 py-3 text-left">Acción</th>
                <th className="px-4 py-3 text-left">Detalle</th>
                <th className="px-4 py-3 text-left w-28">Empresa</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, idx) => {
                const mod = getModuleStyle(log.modulo);
                const acc = getAccionStyle(log.accion);
                const rel = fmtRelative(log.fecha);
                return (
                  <motion.tr
                    key={log.id || idx}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: Math.min(idx * 0.008, 0.25) }}
                    onClick={() => setSelectedLog(log)}
                    className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-all group"
                  >
                    {/* Fecha */}
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      <div className="text-slate-300">{fmtDate(log.fecha)}</div>
                      <div className="text-slate-600">{fmtTime(log.fecha)}</div>
                      {rel && <div className="text-slate-700 text-[10px] mt-0.5">{rel}</div>}
                    </td>

                    {/* Usuario */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColor(log.usuario_nombre)}`}>
                          {initials(log.usuario_nombre)}
                        </div>
                        <div className="min-w-0">
                          <div className="text-white text-sm font-medium leading-tight truncate max-w-[120px]">
                            {log.usuario_nombre || "—"}
                          </div>
                          {log.usuario_email && (
                            <div className="text-slate-600 text-[10px] truncate max-w-[120px]">{log.usuario_email}</div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Módulo */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${mod.bg} ${mod.color}`}>
                        {mod.icon}
                        <span className="hidden md:inline">{mod.label}</span>
                      </span>
                    </td>

                    {/* Acción */}
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${acc.color}`}>
                        {log.accion}
                      </span>
                    </td>

                    {/* Detalle */}
                    <td className="px-4 py-3 text-slate-400 text-xs max-w-[280px]">
                      <span className="line-clamp-2">
                        {log.detalle ? cleanDetalle(log.detalle) : <span className="text-slate-700">—</span>}
                      </span>
                    </td>

                    {/* Empresa */}
                    <td className="px-4 py-3">
                      {log.logo_tipo ? (
                        <span className="text-xs text-slate-400 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">
                          {log.logo_tipo}
                        </span>
                      ) : (
                        <span className="text-slate-700 text-xs">—</span>
                      )}
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Panel usuarios */}
      <div ref={usuariosRef} className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-white">Actividad de usuarios</span>
            <span className="text-xs bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 rounded-full px-2 py-0.5">
              {activeNow} activos ahora
            </span>
            <span className="text-xs text-slate-600">de {totalUsers} total</span>
          </div>
          <div className="flex items-center gap-2">
            {lastRefreshUsuarios && (
              <span className="text-xs text-slate-700">
                {lastRefreshUsuarios.toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            <button onClick={fetchUsuarios} disabled={loadingUsuarios}
              className="p-1.5 text-slate-500 hover:text-white transition-colors rounded" title="Refrescar">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingUsuarios ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {loadingUsuarios && usuarios.length === 0 ? (
          <div className="px-4 py-6 text-center text-slate-600 text-sm animate-pulse">Cargando…</div>
        ) : usuarios.length === 0 ? (
          <div className="px-4 py-6 text-center">
            <Users className="w-10 h-10 text-slate-700 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">No hay usuarios disponibles</p>
          </div>
        ) : (
          <div>
            {usuarios.map((u, idx) => {
              const roleStyle = ROLE_LABEL[u.role] || ROLE_LABEL.user;
              return (
                <div key={u.id || idx}
                  className="flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
                  {/* Dot estado */}
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    u.activo_ahora
                      ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)] animate-pulse"
                      : "bg-slate-700"
                  }`} />

                  {/* Avatar */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarColor(u.name)}`}>
                    {initials(u.name)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white font-medium truncate">{u.name || "—"}</span>
                      {!u.cuenta_activa && (
                        <span className="text-xs text-slate-600 border border-slate-700 rounded px-1">inactivo</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 truncate">{u.email}</div>
                  </div>

                  {/* Rol */}
                  <span className={`text-xs px-2 py-0.5 rounded-full border hidden sm:inline-flex ${roleStyle.color}`}>
                    {roleStyle.label}
                  </span>

                  {/* Estado actividad */}
                  <UserStatusBadge u={u} />

                  {/* Botón forzar logout — solo si tiene sesión activa y el caller puede */}
                  {u.activo_ahora && canForceLogout(user, u) && (
                    <button
                      onClick={() => handleForceLogout(u)}
                      disabled={forcingLogout === u.id}
                      title="Cerrar sesión forzosamente"
                      className="ml-1 p-1.5 rounded-lg text-red-400 hover:bg-red-500/15 hover:text-red-300 transition-colors disabled:opacity-40"
                    >
                      {forcingLogout === u.id
                        ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        : <LogOut className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="px-4 py-2.5 border-t border-white/5 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> Sesión activa ahora
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className="w-2 h-2 rounded-full bg-slate-700 inline-block" /> Sin sesión activa
          </div>
          <span className="text-xs text-slate-700 ml-auto">Auto-refresca cada 30s · Sesiones expiran a las 24h</span>
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedLog && <LogModal log={selectedLog} onClose={() => setSelectedLog(null)} />}
      </AnimatePresence>
    </div>
  );
};

export default AuditoriaPage;
